import React, { useMemo, useState } from "react";
import { clone, isEmpty, isNil } from "ramda";
import { useIntl } from "react-intl";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  MenuItem,
  TextField,
  ThemeProvider,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { getMuiTheme } from "../../../utils/utils";
import palette from "../../../../config/colorPalette";
import { ErrorText } from "../styled";
import { postAlertConfiguration } from "../../../api";
import { setConfigDataAction } from "../../../redux/configSlice";

const AlertConfig = ({ open, setOpen, configData }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const theme = useMemo(() => getMuiTheme(), []);

  const [alertName, setAlertName] = useState("");
  const [alertReference, setAlertReference] = useState("");
  const [alertOrigin, setAlertOrigin] = useState("");
  const [alertActive, setAlertActive] = useState(true);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertThresholdTypes, setAlertThresholdTypes] = useState([]);
  const [alertTresholdConditions, setAlertTresholdConditions] = useState([]);
  const [alertThresholdValues, setAlertThresholdValues] = useState([]);
  const [errors, setErrors] = useState("");
  const handleClose = (setOpen) => {
    setOpen(false);
  };

  return (
    <ThemeProvider theme={theme}>
      <Dialog maxWidth="lg" open={open} onClose={() => handleClose(setOpen)}>
        <DialogTitle style={{ fontSize: "x-large", color: palette.primary, fontWeight: "600" }}>
          {`${intl.formatMessage({ id: "alert.config" })}`}
        </DialogTitle>
        <DialogContent style={{ paddingBottom: "0" }}>
          <form autoComplete="off">
            <div>
              <Divider style={{ marginBottom: "1rem", color: palette.primary }} />
              <div style={{ display: "flex" }}>
                <FormControl>
                  <TextField
                    autoFocus
                    value={alertName}
                    variant="outlined"
                    autoComplete="off"
                    margin="dense"
                    id="alert-name"
                    label={intl.formatMessage({ id: "name" })}
                    type="text"
                    fullWidth
                    required
                    error={errors["alert-name"] ? true : false}
                    onChange={(e) => {
                      setAlertName(e.target.value);
                    }}
                  ></TextField>
                  {!isNil(errors["alert-name"]) && (
                    <ErrorText>{intl.formatMessage({ id: errors["alert-name"] })}</ErrorText>
                  )}
                  <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                    <Grid item>
                      <TextField
                        id="alertReference"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertReference}
                        onChange={(e) => setAlertReference(e.target.value)}
                        select
                        label={intl.formatMessage({ id: "alert.reference" })}
                        required
                      >
                        <MenuItem key={`ref_temperature`} value={"temperature"}>
                          {intl.formatMessage({ id: "temperature" })}
                        </MenuItem>
                        {/**configData.demarcations.map((dem, demIndex) => {
                                    return (
                                      <MenuItem key={`dem_${dem}_${demIndex}`} value={dem}>
                                        {dem}
                                      </MenuItem>
                                    );
                                  })*/}
                      </TextField>
                    </Grid>
                    <Grid item>
                      <TextField
                        id="alertOrigin"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertOrigin}
                        onChange={(e) => setAlertOrigin(e.target.value)}
                        select
                        label={intl.formatMessage({ id: "alert.origin" })}
                        required
                      >
                        <MenuItem key={`origin_equipment`} value={"equipment"}>
                          {intl.formatMessage({ id: "equipment" })}
                        </MenuItem>
                        <MenuItem key={`origin_measure`} value={"measure"}>
                          {intl.formatMessage({ id: "measure" })}
                        </MenuItem>
                        <MenuItem key={`origin_sensor`} value={"sensor"}>
                          {intl.formatMessage({ id: "sensor" })}
                        </MenuItem>
                        <MenuItem key={`origin_external_service`} value={"external_service"}>
                          {intl.formatMessage({ id: "external.service" })}
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>
                  <Grid container spacing={1} direction="row" justifyContent="flex-start" alignItems="flex-start">
                    <Grid item>
                      <FormControlLabel
                        control={
                          <Checkbox
                            id={"checkbox_active"}
                            sx={{
                              color: palette.primary,
                              "&.Mui-checked": {
                                color: palette.primary,
                              },
                              marginLeft: "15%",
                              marginTop: "0.5rem",
                            }}
                            defaultChecked={alertActive}
                            onChange={(e) => {
                              setAlertActive(e.target.checked);
                            }}
                          />
                        }
                        label={intl.formatMessage({ id: "active" })}
                      />
                    </Grid>
                    <Grid item>
                      <TextField
                        id="alertSeverity"
                        margin="dense"
                        sx={{ width: "10rem" }}
                        variant="outlined"
                        value={alertSeverity}
                        onChange={(e) => setAlertSeverity(e.target.value)}
                        select
                        label={intl.formatMessage({ id: "alert.severity" })}
                        required
                      >
                        <MenuItem key={`severity_info`} value={1}>
                          {intl.formatMessage({ id: "info" })}
                        </MenuItem>
                        <MenuItem key={`severity_warning`} value={2}>
                          {intl.formatMessage({ id: "warning" })}
                        </MenuItem>
                        <MenuItem key={`severity_critical`} value={3}>
                          {intl.formatMessage({ id: "critical" })}
                        </MenuItem>
                      </TextField>
                    </Grid>
                  </Grid>

                  <h2>{intl.formatMessage({ id: "thresholds" })}</h2>
                  <Button
                    variant="contained"
                    style={{ backgroundColor: palette.primary, color: "white", width: "6rem", marginTop: "1%" }}
                    onClick={() => {
                      setAlertThresholdTypes([...alertThresholdTypes, "normal"]);
                      setAlertTresholdConditions([...alertTresholdConditions, ">"]);
                      setAlertThresholdValues([...alertThresholdValues, 0]);
                    }}
                  >
                    {intl.formatMessage({ id: "add.threshold" })}
                  </Button>
                  {!isEmpty(alertThresholdTypes) &&
                    alertThresholdTypes.map((at, atIndex) => {
                      return (
                        <Grid
                          spacing={1}
                          container
                          direction="row"
                          justifyContent="center"
                          sx={{ marginTop: "1%" }}
                          alignItems="flex-start"
                        >
                          <Grid item>
                            <TextField
                              id={`alertThresholdTypes${atIndex}`}
                              margin="dense"
                              sx={{ width: "10rem" }}
                              variant="outlined"
                              value={at}
                              onChange={(e) => {
                                let newTypes = clone(alertThresholdTypes);
                                newTypes[atIndex] = e.target.value;
                                setAlertThresholdTypes(newTypes);
                              }}
                              select
                              label={intl.formatMessage({ id: "threshold.type" })}
                              required
                            >
                              <MenuItem key={`threhsold_type_normal`} value={"normal"}>
                                {intl.formatMessage({ id: "normal" })}
                              </MenuItem>
                              <MenuItem key={`threhsold_type_average`} value={"average"}>
                                {intl.formatMessage({ id: "average" })}
                              </MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item>
                            <TextField
                              id={`alertThresholdCondition${atIndex}`}
                              margin="dense"
                              sx={{ width: "10rem" }}
                              variant="outlined"
                              value={alertTresholdConditions[atIndex]}
                              onChange={(e) => {
                                let newConditions = clone(alertTresholdConditions);
                                newConditions[atIndex] = e.target.value;
                                setAlertTresholdConditions(newConditions);
                              }}
                              select
                              label={intl.formatMessage({ id: "threshold.condition" })}
                              required
                            >
                              <MenuItem key={`threhsold_condition_>`} value={">"}>
                                {`>`}
                              </MenuItem>
                              <MenuItem key={`threhsold_condition_>=`} value={">="}>
                                {`>=`}
                              </MenuItem>
                              <MenuItem key={`threhsold_condition_==`} value={"=="}>
                                {`==`}
                              </MenuItem>
                              <MenuItem key={`threhsold_condition_<=`} value={"<="}>
                                {`<=`}
                              </MenuItem>
                              <MenuItem key={`threhsold_condition_<`} value={"<"}>
                                {`<`}
                              </MenuItem>
                            </TextField>
                          </Grid>
                          <Grid item>
                            <TextField
                              value={alertThresholdValues[atIndex]}
                              variant="outlined"
                              autoComplete="off"
                              margin="dense"
                              id={`alertThresholdValue_${atIndex}`}
                              label={intl.formatMessage({ id: "value" })}
                              type="number"
                              fullWidth
                              required
                              error={errors[`alertThresholdValue_${atIndex}`] ? true : false}
                              onChange={(e) => {
                                let newValues = clone(alertThresholdValues);
                                newValues[atIndex] = e.target.value;
                                setAlertThresholdValues(newValues);
                              }}
                              typ
                            ></TextField>
                          </Grid>
                        </Grid>
                      );
                    })}
                </FormControl>
              </div>
              <Divider style={{ marginTop: "1rem" }} />
            </div>
          </form>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            style={{ backgroundColor: palette.secondary, color: "white" }}
            onClick={() => handleClose(setOpen)}
          >
            {intl.formatMessage({ id: "cancel" })}
          </Button>
          {
            <Button
              variant="contained"
              style={{
                //backgroundColor: disabled ? palette.light : palette.primary,
                backgroundColor: palette.primary,
              }}
              id={"save_data_button"}
              //disabled={disabled}
              onClick={async () => {
                const thresholds = [];
                for (let index = 0; index < alertThresholdTypes.length; index++) {
                  const newTH = {
                    type: alertThresholdTypes[index],
                    condition: alertTresholdConditions[index],
                    value: alertThresholdValues[index],
                  };
                  thresholds.push(newTH);
                }
                const finalData = {
                  name: alertName,
                  desc: null,
                  reference: alertReference,
                  origin: alertOrigin,
                  category: null,
                  active: alertActive,
                  severity: alertSeverity,
                  conditions: thresholds,
                };
                const response = await postAlertConfiguration(finalData, intl);
                if (response?.status === 200) {
                  dispatch(
                    setConfigDataAction({
                      ...configData,
                      alertConfiguration: [...configData.alertConfiguration, finalData],
                    }),
                  );
                  handleClose(setOpen);
                }
              }}
            >
              {intl.formatMessage({ id: "accept" })}
            </Button>
          }
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

export default AlertConfig;
